import {
  RoutineWorkflow,
  useRoutineAlerts,
  useWorkspaceAsset,
} from '@gripp/shared-logic';
import {
  AlertSummary,
  Button,
  Colors,
  ColumnData,
  Icon,
  LoadingIndicator,
  SlideAction,
  SlideLayout,
  tokens,
  ViewMode,
} from '@gripp/shared-ui';
import { EditAlertForm } from 'libs/shared-ui/src/components/new-alerts/components/editAlertForm';
import { NewAlertForm } from 'libs/shared-ui/src/components/new-alerts/components/newAlertForm';
import { MutableRefObject, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { useNavigate, useParams } from 'react-router-dom';

type FormRef = {
  handleSubmit: (fn: (data: any) => Promise<void>) => () => void;
  reset: () => void;
} | null;

// ─── Alert list (left column) ────────────────────────────────────────────────

type AlertListContentProps = {
  assetId: string;
  routineId: string;
};

const AlertListContent = ({ assetId, routineId }: AlertListContentProps) => {
  const navigate = useNavigate();
  const { workspaceAsset } = useWorkspaceAsset(assetId);
  const { routineWorkflows, isLoading } = useRoutineAlerts(
    workspaceAsset?.id ?? '',
    routineId
  );
  const { t } = useTranslation();

  const handleAlertSelected = (alert: RoutineWorkflow) => {
    if (!alert?.id) return;
    navigate(
      `/assets/${assetId}/routines/${routineId}/alerts/${alert.id}/edit`
    );
  };

  const handleShowNewAlert = () => {
    navigate(`/assets/${assetId}/routines/${routineId}/alerts/new`);
  };

  if (isLoading || !workspaceAsset) {
    return <LoadingIndicator flexContainer />;
  }

  return (
    <View style={listStyles.container}>
      <ScrollView>
        {routineWorkflows?.map((item: RoutineWorkflow, index: number) => (
          <AlertSummary
            key={`${index}-${item.id}`}
            workflow={item}
            workspaceAsset={workspaceAsset}
            routineId={routineId}
            onAlertSelected={handleAlertSelected}
          />
        ))}
        <Button
          variant="rectangular"
          size="large"
          state="default"
          onPress={handleShowNewAlert}
          leftIcon={
            <Icon
              name="Add"
              size={20}
              color={tokens.colors.brand.black}
              style={{ marginRight: tokens.spacing.md }}
            />
          }
          style={listStyles.createAlertButton}
        >
          {t('alerts.setAlert')}
        </Button>
      </ScrollView>
    </View>
  );
};

// ─── Form header (right column headerComponent) ───────────────────────────────

type AlertFormHeaderProps = {
  formRef: MutableRefObject<FormRef>;
};

const AlertFormHeader = ({ formRef }: AlertFormHeaderProps) => (
  <Pressable
    onPress={() => formRef.current?.reset()}
    style={formStyles.resetButton}
  >
    <Icon name="Reset" size={20} color={Colors.black} />
  </Pressable>
);

// ─── Form content (right column) ─────────────────────────────────────────────

type AlertFormContentProps = {
  assetId: string;
  routineId: string;
  alertId?: string;
  onBack: () => void;
  formRef: MutableRefObject<FormRef>;
  onRegisterBackGuard: (
    guard: (onProceed: () => void, onCancel?: () => void) => void
  ) => void;
};

const AlertFormContent = ({
  assetId,
  routineId,
  alertId,
  onBack,
  formRef,
  onRegisterBackGuard,
}: AlertFormContentProps) => {
  const { workspaceAsset } = useWorkspaceAsset(assetId);
  const { t } = useTranslation();
  const isEdit = !!alertId;

  if (!workspaceAsset) return null;

  const confirmBottomComponent = (onSubmit: () => void, disabled: boolean) => (
    <View>
      <Button
        variant="rectangular"
        size="large"
        state={disabled ? 'default' : 'active'}
        disabled={disabled}
        onPress={() => {
          if (!disabled) {
            onSubmit();
          }
        }}
        style={formStyles.confirmButton}
      >
        {t('alerts.confirm')}
      </Button>
    </View>
  );

  return (
    <View style={formStyles.container}>
      {isEdit ? (
        <EditAlertForm
          key={alertId}
          alertId={alertId}
          routineId={routineId}
          workspaceAssetId={workspaceAsset.id}
          onClose={onBack}
          formRef={formRef}
          renderBottomComponent={confirmBottomComponent}
          onRegisterBackGuard={onRegisterBackGuard}
        />
      ) : (
        <NewAlertForm
          routineId={routineId}
          workspaceAssetId={workspaceAsset.id}
          onClose={onBack}
          formRef={formRef}
          renderBottomComponent={confirmBottomComponent}
          onRegisterBackGuard={onRegisterBackGuard}
        />
      )}
    </View>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export const AssetAlertFormPage = () => {
  const { assetId, routineId, alertId } = useParams<{
    assetId: string;
    routineId: string;
    alertId?: string;
  }>();
  const navigate = useNavigate();
  const formRef = useRef<FormRef>(null);
  const backGuardRef = useRef<
    ((onProceed: () => void, onCancel?: () => void) => void) | null
  >(null);

  if (!assetId || !routineId) return null;

  const routineUrl = `/assets/${assetId}/routines/${routineId}`;

  const backButtonGuard = (): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      if (!backGuardRef.current) {
        resolve(true);
        return;
      }
      backGuardRef.current(
        () => resolve(true),
        () => resolve(false)
      );
    });
  };

  const leftColumn: ColumnData = {
    title: 'alerts.alerts',
    mainComponent: <AlertListContent assetId={assetId} routineId={routineId} />,
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    onBackButtonPress: () => navigate(routineUrl),
    disableScroll: true,
  };

  const rightColumn: ColumnData = {
    title: alertId ? 'alerts.editAlert' : 'alerts.newAlert',
    mainComponent: (
      <AlertFormContent
        assetId={assetId}
        routineId={routineId}
        alertId={alertId}
        onBack={() => navigate(routineUrl)}
        formRef={formRef}
        onRegisterBackGuard={(guard) => {
          backGuardRef.current = guard;
        }}
      />
    ),
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    backButtonGuard,
    onBackButtonPress: () => navigate(`${routineUrl}/alerts`),
    headerComponent: <AlertFormHeader formRef={formRef} />,
    disableScroll: true,
  };

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      viewMode={ViewMode.DUAL}
    />
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const listStyles = StyleSheet.create({
  container: {
    flex: 1,
    margin: tokens.spacing.xl,
  },
  createAlertButton: {
    marginTop: tokens.spacing.md,
  },
});

const formStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
  resetButton: {
    padding: 4,
  },
  confirmButton: {
    width: '100%',
  },
});
